import React, { useState, useEffect } from 'react';
import axios from 'axios';
import qs from 'qs';
import dayjs from 'dayjs';
import {
  FaEye,
  FaEdit,
  FaTrash,
  FaPlus,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaKey,
  FaSave,
  FaSync,
  FaPlug,
  FaCopy
} from 'react-icons/fa';
import Sidebar from './Sidebar';
import { useNavigate } from 'react-router-dom';

// API base URL - replace with your actual API URL
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const ApiMappingScreen = () => {
  // States for API mappings
  const [apiMappings, setApiMappings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showApiForm, setShowApiForm] = useState(false);
  const [showApiView, setShowApiView] = useState(false);
  const [selectedApi, setSelectedApi] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [isScrolled, setIsScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchApiMappings();
  }, []);

  // Add scroll listener for sticky header behavior
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setIsScrolled(scrollTop > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fetch API mappings
  const fetchApiMappings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/api-mappings`);
      if (response.data.success) {
        setApiMappings(response.data.data);
      } else {
        console.error('Error fetching API mappings:', response.data.message);
        setApiMappings([]);
      }
    } catch (err) {
      console.error('Error fetching API mappings:', err);
      setApiMappings([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle sorting
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });

    const data = [...apiMappings];
    
    const sortedData = data.sort((a, b) => {
      if (a[key] === null) return 1;
      if (b[key] === null) return -1;
      
      // Handle date fields
      if (key === 'CreatedAt') {
        return direction === 'asc' 
          ? new Date(a[key]) - new Date(b[key])
          : new Date(b[key]) - new Date(a[key]);
      }
      
      // Handle string fields
      if (a[key] < b[key]) return direction === 'asc' ? -1 : 1;
      if (a[key] > b[key]) return direction === 'asc' ? 1 : -1;
      return 0;
    });

    setApiMappings(sortedData);
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort className="ml-1" />;
    return sortConfig.direction === 'asc' 
      ? <FaSortUp className="ml-1" /> 
      : <FaSortDown className="ml-1" />;
  };

  // Handle API mapping form submission
  const handleApiSubmit = async (values) => {
    setFormLoading(true);
    try {
      if (isEdit && selectedApi) {
        // Update existing API mapping
        const response = await axios.put(`${API_BASE_URL}/api-mappings/${selectedApi.Id}`, values);
        if (!response.data.success) {
          throw new Error(response.data.message || 'Failed to update API mapping');
        }
      } else {
        // Create new API mapping
        const response = await axios.post(`${API_BASE_URL}/api-mappings`, values);
        if (!response.data.success) {
          throw new Error(response.data.message || 'Failed to create API mapping');
        }
      }
      
      setShowApiForm(false);
      fetchApiMappings();
    } catch (err) {
      console.error('Error saving API mapping:', err);
      alert('An error occurred while saving the API mapping: ' + (err.response?.data?.message || err.message));
    } finally {
      setFormLoading(false);
    }
  };

  // View API mapping
  const viewApiMapping = async (record) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api-mappings/${record.Id}`);
      if (response.data.success) {
        setSelectedApi(response.data.data);
        setShowApiView(true);
      } else {
        throw new Error(response.data.message || 'Failed to fetch API mapping');
      }
    } catch (err) {
      console.error('Error fetching API mapping:', err);
      alert('An error occurred while fetching the API mapping: ' + (err.response?.data?.message || err.message));
    }
  };

  // Edit API mapping
  const editApiMapping = async (record) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api-mappings/${record.Id}`);
      if (response.data.success) {
        setSelectedApi(response.data.data);
        setIsEdit(true);
        setShowApiForm(true);
      } else {
        throw new Error(response.data.message || 'Failed to fetch API mapping');
      }
    } catch (err) {
      console.error('Error fetching API mapping:', err);
      alert('An error occurred while fetching the API mapping: ' + (err.response?.data?.message || err.message));
    }
  };

  // Delete API mapping
  const deleteApiMapping = async (id) => {
    if (!window.confirm('Are you sure you want to delete this API mapping?')) return;
    
    try {
      const response = await axios.delete(`${API_BASE_URL}/api-mappings/${id}`);
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to delete API mapping');
      }
      
      fetchApiMappings();
    } catch (err) {
      console.error('Error deleting API mapping:', err);
      alert('An error occurred while deleting the API mapping: ' + (err.response?.data?.message || err.message));
    }
  };

  // Create new API mapping
  const handleCreateApi = () => {
    setSelectedApi(null);
    setIsEdit(false);
    setShowApiForm(true);
  };

  // Handle logout
  const handleLogout = () => {
    navigate('/login');
  };

  // Get HTTP method color
  const getHttpMethodColor = (method) => {
    switch (method) {
      case 'GET': return 'bg-green-100 text-green-800';
      case 'POST': return 'bg-blue-100 text-blue-800';
      case 'PUT': return 'bg-yellow-100 text-yellow-800';
      case 'DELETE': return 'bg-red-100 text-red-800';
      case 'PATCH': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // API Form Modal Component
  const ApiFormModal = ({ open, onClose, onSubmit, initialData, isEdit, loading }) => {
    const [formValues, setFormValues] = useState({
      ApiName: '',
      BaseUrl: '',
      HttpMethod: 'GET',
      TokenUrl: '',
      ClientId: '',
      ClientSecret: '',
      Scope: '',
      GrantType: '',
      UserId: '',
      Password: ''
    });

    useEffect(() => {
      if (initialData) {
        setFormValues({
          ApiName: initialData.ApiName,
          BaseUrl: initialData.BaseUrl,
          HttpMethod: initialData.HttpMethod,
          TokenUrl: initialData.TokenUrl,
          ClientId: initialData.ClientId,
          ClientSecret: initialData.ClientSecret,
          Scope: initialData.Scope,
          GrantType: initialData.GrantType,
          UserId: initialData.UserId,
          Password: initialData.Password
        });
      } else {
        setFormValues({
          ApiName: '',
          BaseUrl: '',
          HttpMethod: 'GET',
          TokenUrl: '',
          ClientId: '',
          ClientSecret: '',
          Scope: '',
          GrantType: '',
          UserId: '',
          Password: ''
        });
      }
    }, [initialData]);

    const handleChange = (e) => {
      const { name, value } = e.target;
      setFormValues(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = () => {
      onSubmit(formValues);
    };

    if (!open) return null;

  return (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
    <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
      {/* Sticky header */}
      <div className="sticky top-0 bg-white border-b px-4 sm:px-6 py-4 flex justify-between items-center z-10">
        <h2 className="text-lg sm:text-xl font-bold text-[#2b6777]">
          {isEdit ? "Edit API Mapping" : "Add API Mapping"}
        </h2>
        <button
          onClick={onClose}
          className="text-gray-500 hover:text-gray-800 transition"
        >
          ✕
        </button>
      </div>

      {/* Form */}
      <div className="p-4 sm:p-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* API Name */}
          <div className="min-w-0">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              API Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="ApiName"
              value={formValues.ApiName}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#92C7CF]"
              placeholder="e.g., RFQ Update API"
              required
            />
          </div>

          {/* Base URL */}
          <div className="min-w-0">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Base URL <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="BaseUrl"
              value={formValues.BaseUrl}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#92C7CF]"
              placeholder="e.g., https://api.example.com"
              required
            />
          </div>

          {/* HTTP Method */}
          <div className="min-w-0">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              HTTP Method
            </label>
            <select
              name="HttpMethod"
              value={formValues.HttpMethod}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#92C7CF]"
            >
              <option value="GET">GET</option>
              <option value="POST">POST</option>
              <option value="PUT">PUT</option>
              <option value="DELETE">DELETE</option>
              <option value="PATCH">PATCH</option>
            </select>
          </div>

          {/* Token URL */}
          <div className="min-w-0">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Token URL
            </label>
            <input
              type="text"
              name="TokenUrl"
              value={formValues.TokenUrl}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#92C7CF]"
              placeholder="e.g., https://auth.example.com/oauth/token"
            />
          </div>

          {/* Client ID */}
          <div className="min-w-0">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Client ID
            </label>
            <input
              type="text"
              name="ClientId"
              value={formValues.ClientId}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#92C7CF]"
              placeholder="e.g., your-client-id"
            />
          </div>

          {/* Client Secret */}
          <div className="min-w-0">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Client Secret
            </label>
            <input
              type="text"
              name="ClientSecret"
              value={formValues.ClientSecret}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#92C7CF]"
              placeholder={
                isEdit
                  ? "Leave blank to keep current secret"
                  : "e.g., your-client-secret"
              }
            />
          </div>

          {/* Scope */}
          <div className="min-w-0">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Scope
            </label>
            <input
              type="text"
              name="Scope"
              value={formValues.Scope}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#92C7CF]"
              placeholder="e.g., api.read api.write"
            />
          </div>

          {/* Grant Type */}
          <div className="min-w-0">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Grant Type
            </label>
            <input
              type="text"
              name="GrantType"
              value={formValues.GrantType}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#92C7CF]"
              placeholder="e.g., client_credentials"
            />
          </div>

          {/* User ID */}
          <div className="min-w-0">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              User ID
            </label>
            <input
              type="text"
              name="UserId"
              value={formValues.UserId}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#92C7CF]"
              placeholder="e.g., username"
            />
          </div>

          {/* Password */}
          <div className="min-w-0">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Password
            </label>
            <input
              type="text"
              name="Password"
              value={formValues.Password}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#92C7CF]"
              placeholder={
                isEdit ? "Leave blank to keep current password" : "e.g., password"
              }
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 mt-8 border-t pt-4">
          <button
            onClick={onClose}
            className="px-5 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="px-5 py-2 bg-[#52ab98] text-white rounded-lg hover:bg-[#2b6777] disabled:opacity-50 flex items-center transition"
          >
            {loading ? (
              <>
                <svg
                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 
                      0 5.373 0 12h4zm2 5.291A7.962 
                      7.962 0 014 12H0c0 3.042 1.135 
                      5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Processing...
              </>
            ) : isEdit ? "Update" : "Create"}
          </button>
        </div>
      </div>
    </div>
  </div>
);


  };

  // API View Modal Component
  const ApiViewModal = ({ open, onClose, api }) => {
    if (!open || !api) return null;

    return (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
    <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
      <div className="p-4 sm:p-6">
        {/* Header */}
        <h2 className="text-lg sm:text-xl font-bold text-[#2b6777] mb-6 border-b pb-3">
          API Mapping Details
        </h2>

        {/* Details list */}
        <dl className="divide-y divide-gray-200">
          <div className="py-3 sm:py-4 grid grid-cols-3 sm:grid-cols-4 gap-2">
            <dt className="text-sm font-medium text-gray-600 col-span-1">API Name</dt>
            <dd className="text-sm font-semibold text-gray-900 col-span-2 sm:col-span-3 break-words">
              {api.ApiName}
            </dd>
          </div>

          <div className="py-3 sm:py-4 grid grid-cols-3 sm:grid-cols-4 gap-2">
            <dt className="text-sm font-medium text-gray-600 col-span-1">Base URL</dt>
            <dd className="text-sm font-semibold text-gray-900 col-span-2 sm:col-span-3 break-all">
              {api.BaseUrl}
            </dd>
          </div>

          <div className="py-3 sm:py-4 grid grid-cols-3 sm:grid-cols-4 gap-2 items-center">
            <dt className="text-sm font-medium text-gray-600 col-span-1">HTTP Method</dt>
            <dd className="col-span-2 sm:col-span-3">
              <span
                className={`px-3 py-1 rounded-full text-xs font-bold inline-block ${getHttpMethodColor(
                  api.HttpMethod
                )}`}
              >
                {api.HttpMethod}
              </span>
            </dd>
          </div>

          <div className="py-3 sm:py-4 grid grid-cols-3 sm:grid-cols-4 gap-2">
            <dt className="text-sm font-medium text-gray-600 col-span-1">Token URL</dt>
            <dd className="text-sm font-semibold text-gray-900 col-span-2 sm:col-span-3 break-all">
              {api.TokenUrl}
            </dd>
          </div>

          <div className="py-3 sm:py-4 grid grid-cols-3 sm:grid-cols-4 gap-2">
            <dt className="text-sm font-medium text-gray-600 col-span-1">Client ID</dt>
            <dd className="text-sm font-semibold text-gray-900 col-span-2 sm:col-span-3">
              {api.ClientId}
            </dd>
          </div>

          <div className="py-3 sm:py-4 grid grid-cols-3 sm:grid-cols-4 gap-2">
            <dt className="text-sm font-medium text-gray-600 col-span-1">Client Secret</dt>
            <dd className="text-sm font-semibold text-gray-900 col-span-2 sm:col-span-3">
              {api.ClientSecret}
            </dd>
          </div>

          <div className="py-3 sm:py-4 grid grid-cols-3 sm:grid-cols-4 gap-2">
            <dt className="text-sm font-medium text-gray-600 col-span-1">Scope</dt>
            <dd className="text-sm font-semibold text-gray-900 col-span-2 sm:col-span-3">
              {api.Scope}
            </dd>
          </div>

          <div className="py-3 sm:py-4 grid grid-cols-3 sm:grid-cols-4 gap-2">
            <dt className="text-sm font-medium text-gray-600 col-span-1">Grant Type</dt>
            <dd className="text-sm font-semibold text-gray-900 col-span-2 sm:col-span-3">
              {api.GrantType}
            </dd>
          </div>

          <div className="py-3 sm:py-4 grid grid-cols-3 sm:grid-cols-4 gap-2">
            <dt className="text-sm font-medium text-gray-600 col-span-1">User ID</dt>
            <dd className="text-sm font-semibold text-gray-900 col-span-2 sm:col-span-3">
              {api.UserId}
            </dd>
          </div>

          <div className="py-3 sm:py-4 grid grid-cols-3 sm:grid-cols-4 gap-2">
            <dt className="text-sm font-medium text-gray-600 col-span-1">Password</dt>
            <dd className="text-sm font-semibold text-gray-900 col-span-2 sm:col-span-3">
              {api.Password}
            </dd>
          </div>

          <div className="py-3 sm:py-4 grid grid-cols-3 sm:grid-cols-4 gap-2">
            <dt className="text-sm font-medium text-gray-600 col-span-1">Created At</dt>
            <dd className="text-sm font-semibold text-gray-900 col-span-2 sm:col-span-3">
              {dayjs(api.CreatedAt).format("YYYY-MM-DD HH:mm")}
            </dd>
          </div>
        </dl>

        {/* Footer */}
        <div className="flex justify-end mt-6">
          <button
            onClick={onClose}
            className="px-5 py-2 bg-[#52ab98] text-white rounded-lg hover:bg-[#2b6777] transition"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  </div>
);

  };

  return (
    <div className="min-h-screen flex bg-[#E5E1DA]">
      <Sidebar onLogout={handleLogout} />
      <main className="flex-1 p-2 md:p-8 overflow-hidden ml-0 md:ml-16 transition-all duration-300">
        <div className="flex flex-col h-full">
          <div className="flex flex-col md:flex-row justify-center sm:justify-between items-center text-center md:items-center mb-4 md:mb-6 gap-4">
            <h1 className="text-2xl md:text-3xl font-bold text-[#2b6777]">API Mapping</h1>
            <button
              className="flex items-center gap-2 bg-[#92C7CF] hover:bg-[#AAD7D9] text-white font-semibold px-4 py-2 rounded-lg shadow transition w-full md:w-auto justify-center"
              onClick={handleCreateApi}
            >
              <FaPlus /> Add API Mapping
            </button>
          </div>
          
          <div className="flex-1 overflow-hidden">
            <div className="bg-white rounded-2xl shadow-lg p-4 mb-4">
              <div className="flex items-center gap-2">
                <FaPlug className="text-[#2b6777]" />
                <h2 className="text-lg font-semibold text-[#2b6777]">API Mappings</h2>
              </div>
            </div>
            
            <div className="h-full overflow-auto overflow-x-hidden rounded-2xl shadow-lg border border-gray-200">
              <table className="w-full bg-white rounded-2xl shadow-lg border-separate border-spacing-0 min-w-[1000px]">
                <thead>
                  <tr>
                    <th 
                      className={`sticky top-0 z-20 bg-[#AAD7D9] w-auto text-[#2b6777] px-4 md:px-6 py-3 md:py-4 text-center border-r border-white text-xs md:text-sm font-bold rounded-tl-2xl cursor-pointer hover:bg-[#92C7CF] transition-all duration-300 ${isScrolled ? 'shadow-lg' : ''}`}
                      onClick={() => handleSort('ApiName')}
                    >
                      <div className="flex items-center justify-center">
                        API Name {getSortIcon('ApiName')}
                      </div>
                    </th>
                    <th 
                      className={`sticky top-0 z-20 bg-[#AAD7D9] text-[#2b6777] px-4 md:px-6 py-3 md:py-4 text-center border-r border-white text-xs md:text-sm font-bold cursor-pointer hover:bg-[#92C7CF] transition-all duration-300 ${isScrolled ? 'shadow-lg' : ''}`}
                      onClick={() => handleSort('BaseUrl')}
                    >
                      <div className="flex items-center justify-center">
                        Base URL {getSortIcon('BaseUrl')}
                      </div>
                    </th>
                    
                    <th 
                      className={`sticky top-0 z-20 bg-[#AAD7D9] text-[#2b6777] px-4 md:px-6 py-3 md:py-4 text-center border-r border-white text-xs md:text-sm font-bold cursor-pointer hover:bg-[#92C7CF] transition-all duration-300 ${isScrolled ? 'shadow-lg' : ''}`}
                      onClick={() => handleSort('TokenUrl')}
                    >
                      <div className="flex items-center justify-center">
                        Token URL {getSortIcon('TokenUrl')}
                      </div>
                    </th>
                   
                    <th 
                      className={`sticky top-0 z-20 bg-[#AAD7D9] text-[#2b6777] px-4 md:px-6 py-3 md:py-4 text-center border-r border-white text-xs md:text-sm font-bold cursor-pointer hover:bg-[#92C7CF] transition-all duration-300 ${isScrolled ? 'shadow-lg' : ''}`}
                      onClick={() => handleSort('CreatedAt')}
                    >
                      <div className="flex items-center justify-center">
                        Created At {getSortIcon('CreatedAt')}
                      </div>
                    </th>
                    <th className={`sticky top-0 z-20 bg-[#AAD7D9] text-[#2b6777] px-4 md:px-6 py-3 md:py-4 text-center text-xs md:text-sm font-bold rounded-tr-2xl transition-all duration-300 ${isScrolled ? 'shadow-lg' : ''}`}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={11} className="text-center py-10 text-[#AAD7D9] font-semibold">
                        Loading API mappings...
                      </td>
                    </tr>
                  ) : apiMappings.length === 0 ? (
                    <tr>
                      <td colSpan={11} className="text-center py-10 text-[#AAD7D9] font-semibold">
                        No API mappings found.
                      </td>
                    </tr>
                  ) : apiMappings.map((api, idx) => (
                    <tr
                      key={api.Id}
                      className="transition-all duration-300 group hover:bg-[#eaf6f6] hover:shadow-md border-b last:border-b-0 transform hover:scale-[1.01]"
                      style={{ boxShadow: idx === apiMappings.length - 1 ? '0 8px 24px 0 rgba(44,103,119,0.04)' : undefined }}
                    >
                      <td className="px-4 md:px-6 py-3 md:py-4 text-center text-xs md:text-sm whitespace-nowrap flex justify-center items-center">
                        <p className='cursor-pointer transition-all duration-200 hover:scale-105' style={{ color: 'blue', borderBottom: '1px solid blue', width: 'fit-content' }} onClick={() => viewApiMapping(api)}>{api.ApiName}</p>
                      </td>
                      <td className="px-4 md:px-6 py-3 md:py-4 text-center text-xs md:text-sm whitespace-nowrap max-w-xs truncate" title={api.BaseUrl}>{api.BaseUrl}</td>
                     
                      <td className="px-4 md:px-6 py-3 md:py-4 text-center text-xs md:text-sm whitespace-nowrap max-w-xs truncate" title={api.TokenUrl}>{api.TokenUrl}</td>
                     
                      <td className="px-4 md:px-6 py-3 md:py-4 whitespace-nowrap text-xs md:text-sm text-gray-700">
                        {dayjs(api.CreatedAt).format('YYYY-MM-DD')}
                        <br />
                        <span className="text-xs text-gray-400">{dayjs(api.CreatedAt).format('HH:mm')}</span>
                      </td>
                      <td className="px-4 md:px-6 py-3 md:py-4 flex gap-2 justify-center items-center">
                        <button title="View" className="p-1 md:p-2 rounded-full hover:bg-[#c8d8e4] text-[#2b6777] transition-all duration-200 hover:scale-110" onClick={() => viewApiMapping(api)}>
                          <FaEye className="text-sm md:text-base" />
                        </button>
                        <button title="Edit" className="p-1 md:p-2 rounded-full hover:bg-[#c8d8e4] text-[#2b6777] transition-all duration-200 hover:scale-110" onClick={() => editApiMapping(api)}>
                          <FaEdit className="text-sm md:text-base" />
                        </button>
                        <button title="Delete" className="p-1 md:p-2 rounded-full hover:bg-red-100 text-red-500 transition-all duration-200 hover:scale-110" onClick={() => deleteApiMapping(api.Id)}>
                          <FaTrash className="text-sm md:text-base" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </main>
      
      {/* Modals */}
      <ApiFormModal
        open={showApiForm}
        onClose={() => setShowApiForm(false)}
        onSubmit={handleApiSubmit}
        initialData={selectedApi}
        isEdit={isEdit}
        loading={formLoading}
      />
      
      <ApiViewModal
        open={showApiView}
        onClose={() => setShowApiView(false)}
        api={selectedApi}
      />
    </div>
  );
};

export default ApiMappingScreen;